import React, { useEffect, useMemo, useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LabelList,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { analyticsAPI } from '../../services/api';

/* ═══════════════ Benevita Polar design tokens ═══════════════ */
const T = {
  primary: '#7B1E3C', primaryLight: '#94335A', sky: '#C39A4E', teal: '#00897B',
  navy: '#2B1B22', danger: '#D32F2F', warning: '#F57C00', info: '#0277BD',
  purple: '#5C35B5', text: '#2B1B22', muted: '#8b7681', border: '#ECE3E7',
};
const SERIES = [T.primary, T.teal, T.sky, T.purple, T.warning, T.info];
const CARD_SHADOW = '0 1px 2px rgba(13,27,46,.04), 0 10px 30px -16px rgba(13,27,46,.10)';
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const cardStyle = {
  background: '#fff', borderRadius: 16, border: `1px solid ${T.border}`,
  boxShadow: CARD_SHADOW, padding: '20px 24px',
};

const AXIS_TICK = { fill: '#8A9BB0', fontSize: 11 };
const axisProps = { axisLine: false, tickLine: false, tick: AXIS_TICK };

/* ═══════════════ Local sub-components ═══════════════ */

function PolarTooltip({ active, payload, label, labelFormatter, valueFormatter }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: '#fff', borderRadius: 12, border: `1px solid ${T.border}`,
      boxShadow: '0 8px 24px -8px rgba(13,27,46,.18)', padding: '10px 12px',
      fontSize: 12, fontFamily: FONT,
    }}>
      {label != null && (
        <div style={{ color: T.muted, fontWeight: 600, marginBottom: 6 }}>
          {labelFormatter ? labelFormatter(label) : label}
        </div>
      )}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: i ? 4 : 0 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: p.color || p.payload?.fill || T.primary,
          }} />
          <span style={{ color: T.muted }}>{p.name}</span>
          <span style={{ color: T.text, fontWeight: 700, marginLeft: 'auto', paddingLeft: 12 }}>
            {valueFormatter ? valueFormatter(p.value) : Number(p.value).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

function SectionTitle({ title, sub }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.text, letterSpacing: '-0.01em' }}>{title}</h3>
      {sub && <p style={{ margin: '3px 0 0', fontSize: 12, color: T.muted }}>{sub}</p>}
    </div>
  );
}

function ChartCard({ title, sub, children, empty, hasData, height = 240, style }) {
  return (
    <div style={{ ...cardStyle, ...style }}>
      <SectionTitle title={title} sub={sub} />
      {hasData ? children : (
        <div style={{
          height, display: 'flex', flexDirection: 'column', gap: 8,
          alignItems: 'center', justifyContent: 'center', color: T.muted, fontSize: 13,
          background: '#F8FAFD', borderRadius: 12, border: `1px dashed ${T.border}`,
        }}>
          <span style={{ fontSize: 22, opacity: .5 }}>◌</span>
          {empty}
        </div>
      )}
    </div>
  );
}

function PeriodSegment({ value, onChange }) {
  const opts = [7, 14, 30, 90];
  return (
    <div style={{
      display: 'inline-flex', background: '#EEF3F9', borderRadius: 999, padding: 4, gap: 2,
    }}>
      {opts.map(d => {
        const active = value === d;
        return (
          <button key={d} onClick={() => onChange(d)} style={{
            padding: '7px 16px', borderRadius: 999, border: 'none', cursor: 'pointer',
            fontFamily: FONT, fontSize: 12.5, fontWeight: 700,
            background: active ? '#fff' : 'transparent',
            color: active ? T.primary : T.muted,
            boxShadow: active ? '0 1px 3px rgba(13,27,46,.12), 0 4px 10px -4px rgba(13,27,46,.12)' : 'none',
            transition: 'background 150ms ease, color 150ms ease, box-shadow 150ms ease',
          }}>
            {d}d
          </button>
        );
      })}
    </div>
  );
}

function Sparkline({ data, dataKey, color, id }) {
  if (!data || data.length < 2) return null;
  return (
    <div style={{ height: 40, marginTop: 10 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.25} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2}
            fill={`url(#${id})`} dot={false} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function DeltaPill({ delta }) {
  if (delta == null || !isFinite(delta)) return null;
  const up = delta >= 0;
  const color = up ? T.teal : T.danger;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 8,
      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
      color, background: `${color}14`, border: `1px solid ${color}33`,
    }}>
      {up ? '▲' : '▼'} {Math.abs(delta).toFixed(0)}%
    </span>
  );
}

function MetricCard({ label, value, accent, spark, sparkKey, sparkId, delta }) {
  return (
    <div style={{ ...cardStyle, padding: '16px 18px 12px', position: 'relative', overflow: 'hidden' }}>
      <span style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${accent}, ${accent}00)`, opacity: .65,
      }} />
      <div style={{ fontSize: 13, fontWeight: 600, color: T.muted }}>{label}</div>
      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: 24, fontWeight: 800, color: T.text, letterSpacing: '-0.02em' }}>{value}</span>
        <DeltaPill delta={delta} />
      </div>
      {spark && spark.length > 1 && (
        <Sparkline data={spark} dataKey={sparkKey} color={accent} id={sparkId} />
      )}
    </div>
  );
}

function InsightCard({ icon, accent, caption, stat, detail }) {
  return (
    <div style={{ ...cardStyle, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{
        width: 42, height: 42, borderRadius: 12, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${accent}14`, border: `1px solid ${accent}26`, fontSize: 19,
      }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>{caption}</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {stat}
        </div>
        {detail && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1 }}>{detail}</div>}
      </div>
    </div>
  );
}

function DonutLegend({ data, total }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14, justifyContent: 'center' }}>
      {data.map((s, i) => {
        const pct = total > 0 ? (s.count / total) * 100 : 0;
        return (
          <span key={i} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
            color: T.text, background: '#F4F8FC', border: `1px solid ${T.border}`,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: SERIES[i % SERIES.length] }} />
            {prettyService(s.service_type)}
            <span style={{ color: T.muted }}>{Number(s.count).toLocaleString()} · {pct.toFixed(0)}%</span>
          </span>
        );
      })}
    </div>
  );
}

function SkeletonPage() {
  return (
    <div style={{ fontFamily: FONT }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <div className="hhms-skeleton" style={{ width: 220, height: 44, borderRadius: 12, background: '#EEF3F9' }} />
        <div className="hhms-skeleton" style={{ width: 220, height: 36, borderRadius: 999, background: '#EEF3F9' }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="hhms-skeleton" style={{ height: 92, borderRadius: 16, background: '#EEF3F9' }} />
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="hhms-skeleton" style={{ height: 300, borderRadius: 16, background: '#EEF3F9' }} />
        ))}
      </div>
    </div>
  );
}

/* ═══════════════ helpers ═══════════════ */
function prettyService(s) {
  return String(s || 'Other').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
function fmtKES(v) { return `KES ${Number(v).toLocaleString()}`; }

/* ═══════════════ Page ═══════════════ */
export default function Analytics() {
  const [dash, setDash] = useState(null);
  const [visitsTrend, setVisitsTrend] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [serviceBreakdown, setServiceBreakdown] = useState([]);
  const [nursePerf, setNursePerf] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    Promise.allSettled([
      analyticsAPI.dashboard(),
      analyticsAPI.visitsTrend(period),
      analyticsAPI.revenueTrend(6),
      analyticsAPI.serviceBreakdown(),
      analyticsAPI.nursePerformance(10),
    ]).then(([d, v, r, s, n]) => {
      if (d.status === 'fulfilled') setDash(d.value?.data || d.value);
      if (v.status === 'fulfilled') setVisitsTrend(v.value?.data || []);
      if (r.status === 'fulfilled') setRevenueTrend(r.value?.data || []);
      if (s.status === 'fulfilled') setServiceBreakdown(s.value?.data || []);
      if (n.status === 'fulfilled') setNursePerf(n.value?.data || []);
      setLoading(false);
    });
  }, [period]);

  /* ── derived insights from already-loaded data ── */
  const serviceTotal = useMemo(
    () => serviceBreakdown.reduce((a, s) => a + Number(s.count || 0), 0),
    [serviceBreakdown]
  );

  const visitsDelta = useMemo(() => {
    if (visitsTrend.length < 4) return null;
    const half = Math.floor(visitsTrend.length / 2);
    const prev = visitsTrend.slice(0, half).reduce((a, p) => a + Number(p.count || 0), 0);
    const curr = visitsTrend.slice(half).reduce((a, p) => a + Number(p.count || 0), 0);
    if (prev <= 0) return null;
    return ((curr - prev) / prev) * 100;
  }, [visitsTrend]);

  const revenueDelta = useMemo(() => {
    if (revenueTrend.length < 2) return null;
    const prev = Number(revenueTrend[revenueTrend.length - 2]?.revenue || 0);
    const curr = Number(revenueTrend[revenueTrend.length - 1]?.revenue || 0);
    if (prev <= 0) return null;
    return ((curr - prev) / prev) * 100;
  }, [revenueTrend]);

  const insights = useMemo(() => {
    const out = [];
    if (visitsTrend.length > 0) {
      const busiest = visitsTrend.reduce((m, p) => (Number(p.count || 0) > Number(m.count || 0) ? p : m), visitsTrend[0]);
      if (busiest?.date && Number(busiest.count || 0) > 0) {
        let dayLabel = busiest.date;
        const dt = new Date(busiest.date);
        if (!isNaN(dt)) dayLabel = dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
        out.push({ icon: '📅', accent: T.primary, caption: 'Busiest day', stat: dayLabel, detail: `${busiest.count} visits` });
      }
    }
    if (serviceBreakdown.length > 0 && serviceTotal > 0) {
      const top = serviceBreakdown.reduce((m, s) => (Number(s.count || 0) > Number(m.count || 0) ? s : m), serviceBreakdown[0]);
      out.push({
        icon: '🩺', accent: T.teal, caption: 'Top service',
        stat: prettyService(top.service_type),
        detail: `${Number(top.count).toLocaleString()} requests · ${((top.count / serviceTotal) * 100).toFixed(0)}%`,
      });
    }
    if (nursePerf.length > 0) {
      const n = nursePerf[0];
      out.push({
        icon: '🏅', accent: T.purple, caption: 'Top performer',
        stat: n.name || 'Nurse 1',
        detail: `${n.total_visits ?? '—'} visits${n.rating ? ` · ⭐ ${Number(n.rating).toFixed(1)}` : ''}`,
      });
    }
    if (revenueTrend.length > 0) {
      const best = revenueTrend.reduce((m, r) => (Number(r.revenue || 0) > Number(m.revenue || 0) ? r : m), revenueTrend[0]);
      if (Number(best.revenue || 0) > 0) {
        out.push({ icon: '💎', accent: T.warning, caption: 'Best revenue month', stat: best.month, detail: fmtKES(best.revenue) });
      }
    }
    return out;
  }, [visitsTrend, serviceBreakdown, serviceTotal, nursePerf, revenueTrend]);

  const nurseChartData = useMemo(
    () => nursePerf.slice(0, 8).map((n, i) => ({
      name: n.name || `Nurse ${i + 1}`,
      visits: Number(n.total_visits || 0),
      rating: n.rating ? Number(n.rating).toFixed(1) : null,
    })),
    [nursePerf]
  );

  if (loading) return <SkeletonPage />;

  const metrics = [
    { label: 'Total Patients', value: dash?.total_patients ?? '—', accent: T.primary },
    { label: 'Active Nurses', value: dash?.available_nurses ?? dash?.total_nurses ?? '—', accent: T.teal },
    { label: 'Visits Today', value: dash?.visits_today ?? '—', accent: T.purple, spark: visitsTrend, sparkKey: 'count', sparkId: 'spk-visits', delta: visitsDelta },
    { label: 'Pending Requests', value: dash?.active_requests ?? '—', accent: T.warning },
    { label: 'Completed Visits', value: dash?.completed_visits ?? '—', accent: T.sky },
    { label: 'Revenue (KES)', value: `${Number(dash?.total_revenue || 0).toLocaleString()}`, accent: T.info, spark: revenueTrend, sparkKey: 'revenue', sparkId: 'spk-rev', delta: revenueDelta },
    { label: 'Pending Billing', value: dash?.pending_billing ?? '—', accent: T.danger },
    { label: 'Active Care Plans', value: dash?.active_care_plans ?? '—', accent: T.primaryLight },
  ];

  return (
    <div style={{ fontFamily: FONT, color: T.text }}>
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes hhmsFadeInUp {
            from { opacity: 0; transform: translateY(14px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .hhms-an-section { opacity: 0; animation: hhmsFadeInUp .55s cubic-bezier(.22,.8,.3,1) forwards; }
          .hhms-an-s1 { animation-delay: .02s; }
          .hhms-an-s2 { animation-delay: .10s; }
          .hhms-an-s3 { animation-delay: .18s; }
          .hhms-an-s4 { animation-delay: .26s; }
          .hhms-an-s5 { animation-delay: .34s; }
        }
      `}</style>

      {/* 1 ─ Header */}
      <div className="hhms-an-section hhms-an-s1" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        flexWrap: 'wrap', gap: 14, marginBottom: 22,
      }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: 0, letterSpacing: '-0.02em' }}>Analytics</h1>
          <p style={{ color: T.muted, fontSize: 13, margin: '4px 0 0' }}>
            System-wide performance metrics and trends
          </p>
        </div>
        <PeriodSegment value={period} onChange={setPeriod} />
      </div>

      {/* 2 ─ Headline metrics strip */}
      <div className="hhms-an-section hhms-an-s2" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 14, marginBottom: 18,
      }}>
        {metrics.map(m => <MetricCard key={m.label} {...m} />)}
      </div>

      {/* 3 ─ Main charts */}
      <div className="hhms-an-section hhms-an-s3" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: 16, marginBottom: 16,
      }}>
        <ChartCard
          title={`Visit Trend — Last ${period} days`} sub="Daily visits completed"
          hasData={visitsTrend.length > 0} empty="No trend data yet — visits data will appear here"
        >
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={visitsTrend} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
              <defs>
                <linearGradient id="gradVisits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={T.primary} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={T.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 6" stroke="#E8EEF5" />
              <XAxis dataKey="date" {...axisProps} tickFormatter={v => v?.slice(5)} />
              <YAxis {...axisProps} allowDecimals={false} />
              <Tooltip content={<PolarTooltip />} cursor={{ stroke: T.border }} />
              <Area type="monotone" dataKey="count" name="Visits" stroke={T.primary}
                strokeWidth={2.5} fill="url(#gradVisits)" dot={false}
                activeDot={{ r: 5, stroke: '#fff', strokeWidth: 2, fill: T.primary }} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Service Distribution" sub="By service type requested"
          hasData={serviceBreakdown.length > 0} empty="No service data yet"
        >
          <div style={{ position: 'relative' }}>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={serviceBreakdown} cx="50%" cy="50%"
                  innerRadius="62%" outerRadius="90%" paddingAngle={2} cornerRadius={6}
                  dataKey="count" nameKey="service_type" stroke="none"
                >
                  {serviceBreakdown.map((_, i) => <Cell key={i} fill={SERIES[i % SERIES.length]} />)}
                </Pie>
                <Tooltip content={<PolarTooltip valueFormatter={v => Number(v).toLocaleString()} />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
            }}>
              <span style={{ fontSize: 22, fontWeight: 800, color: T.text }}>{serviceTotal.toLocaleString()}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '.06em' }}>Total</span>
            </div>
          </div>
          <DonutLegend data={serviceBreakdown} total={serviceTotal} />
        </ChartCard>
      </div>

      <div className="hhms-an-section hhms-an-s4" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: 16, marginBottom: 16,
      }}>
        <ChartCard
          title="Revenue Trend" sub="Monthly revenue (KES)"
          hasData={revenueTrend.length > 0} empty="No revenue data yet" height={220}
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={revenueTrend} margin={{ top: 6, right: 8, bottom: 0, left: -6 }}>
              <defs>
                <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={T.teal} stopOpacity={1} />
                  <stop offset="100%" stopColor="#26A69A" stopOpacity={0.75} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 6" stroke="#E8EEF5" />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis {...axisProps} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<PolarTooltip valueFormatter={fmtKES} />} cursor={{ fill: '#F4F8FC' }} />
              <Bar dataKey="revenue" name="Revenue" fill="url(#gradRevenue)"
                radius={[8, 8, 0, 0]} maxBarSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Top Nurse Performance" sub="Completed visits per nurse"
          hasData={nurseChartData.length > 0} empty="No performance data yet" height={220}
        >
          <ResponsiveContainer width="100%" height={Math.max(220, nurseChartData.length * 38)}>
            <BarChart data={nurseChartData} layout="vertical" margin={{ top: 0, right: 36, bottom: 0, left: 8 }}>
              <defs>
                <linearGradient id="gradNurse" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={T.primary} stopOpacity={1} />
                  <stop offset="100%" stopColor={T.primaryLight} stopOpacity={0.85} />
                </linearGradient>
              </defs>
              <CartesianGrid horizontal={false} strokeDasharray="3 6" stroke="#E8EEF5" />
              <XAxis type="number" {...axisProps} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={110} axisLine={false} tickLine={false}
                tick={{ fill: '#52677F', fontSize: 12, fontWeight: 600 }} />
              <Tooltip content={<PolarTooltip />} cursor={{ fill: '#F4F8FC' }} />
              <Bar dataKey="visits" name="Visits" fill="url(#gradNurse)"
                radius={[0, 8, 8, 0]} maxBarSize={20}>
                <LabelList dataKey="visits" position="right" style={{ fill: T.text, fontSize: 11, fontWeight: 700 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* 4 ─ Insights + compliance */}
      <div className="hhms-an-section hhms-an-s5">
        {insights.length > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 14, marginBottom: 16,
          }}>
            {insights.map(ins => <InsightCard key={ins.caption} {...ins} />)}
          </div>
        )}

        <div style={{ ...cardStyle, padding: '18px 24px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: T.text }}>Standards Compliance</h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[
              { label: 'HL7 FHIR R4', color: T.primary },
              { label: 'ICD-10 / ICD-11', color: T.teal },
              { label: 'SNOMED CT', color: T.purple },
              { label: 'LOINC Vitals', color: T.info },
              { label: 'HIPAA PHI', color: T.warning },
              { label: 'GDPR Article 5/6/17', color: T.danger },
              { label: 'ISO 27001', color: T.muted },
              { label: 'Kenya DPA 2019', color: '#2E7D32' },
              { label: 'IEEE 11073 RPM', color: T.primaryLight },
            ].map(({ label, color }) => (
              <div key={label} style={{
                padding: '5px 12px', background: `${color}12`, border: `1px solid ${color}38`,
                borderRadius: 999, fontSize: 11, fontWeight: 600, color,
              }}>
                ✓ {label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
